/**
 * A tenant's chosen palette, resolved — and the platform's own when they have
 * not chosen one.
 *
 * MOVED HERE FROM `src/app/lib/theme.ts` because `src/api` needs the identical
 * answer and may never import from `src/app`. `POST /api/auth/resolve` hands a
 * phone app the colours it will render, and those must be the same values the
 * web console renders or the two drift into different-looking products for the
 * same company.
 *
 * ⚠️ `branding_set_at` IS THE AUTHORITY, NOT THE COLOUR COLUMNS. `tenants`
 * still declares New Leaf's green as the DEFAULT for `primary_color`, so a
 * company that never opened the branding page has a non-null colour that is
 * somebody else's. The timestamp is the only way to tell a company that
 * deliberately picked that green from one that merely inherited it — read the
 * columns directly and every unbranded tenant renders in New Leaf's brand.
 */

import { DEFAULT_FONT, isFontKey, type FontKey } from '@shared/fonts';

/**
 * Broomstack's own palette — deliberately neutral, and deliberately not any
 * real company's. What a tenant sees before choosing anything.
 */
export const PLATFORM_THEME = {
  primary: '#2C2C2C',
  secondary: '#F1F3F5',
  accent: '#8F8170',
} as const;

export interface TenantTheme {
  primary: string;
  secondary: string;
  accent: string;
  /** Which typeface pairing this company runs on — a key from FONT_CHOICES. */
  font: FontKey;
  /** True when these are the company's own choices rather than the platform's. */
  isCustom: boolean;
}

export interface ThemeInput {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  /** A FONT_CHOICES key. Anything else falls back to the default pairing. */
  brandFont?: string | null;
  brandingSetAt?: number | null;
}

/** Only ever emit a colour we have validated — this string reaches a <style>. */
const HEX = /^#[0-9A-Fa-f]{6}$/;

function safeHex(value: string | null | undefined, fallback: string): string {
  const v = (value ?? '').trim();
  return HEX.test(v) ? v : fallback;
}

/**
 * Resolve one tenant's theme.
 *
 * A company that has not stamped `branding_set_at` gets the platform palette
 * even if stale colour values are sitting in its row — the timestamp is the
 * authority on whether a choice was ever made.
 */
export function resolveTheme(input: ThemeInput | null | undefined): TenantTheme {
  /*
   * The typeface is resolved SEPARATELY from the palette.
   *
   * `branding_set_at` is stamped by choosing a colour, so gating the font on it
   * would mean a company that picked a typeface and no colours kept the default
   * face — a setting that visibly does nothing when you save it.
   */
  const font = isFontKey(input?.brandFont) ? input.brandFont : DEFAULT_FONT;

  const chose = Boolean(input?.brandingSetAt);
  if (!chose) {
    return { ...PLATFORM_THEME, font, isCustom: false };
  }
  return {
    primary: safeHex(input?.primaryColor, PLATFORM_THEME.primary),
    secondary: safeHex(input?.secondaryColor, PLATFORM_THEME.secondary),
    accent: safeHex(input?.accentColor, PLATFORM_THEME.accent),
    font,
    isCustom: true,
  };
}
